/**
 * Centralized meeting permission logic.
 * Single source of truth for creator vs shared attendee role determination.
 */

export interface MeetingPermissions {
  /** True only when the current user is the original meeting creator */
  readonly isCreator: boolean;
  /** True when the current user has edit access (creator or co-organizer) */
  readonly hasEditAccess: boolean;
  /** True when viewing a shared link as a non-editor */
  readonly isSharedViewer: boolean;
  /** True when viewing a shared link as an editor (creator or co-organizer) */
  readonly isSharedCreator: boolean;
  /** True when a shared link is present (meetingId + creator in URL) */
  readonly isSharedLink: boolean;
  /** True when the user should have read-only access (same as isSharedViewer) */
  readonly isReadOnly: boolean;
  /** True when the user can add/delete/reorder time slots */
  readonly canEditSlots: boolean;
}

/**
 * Derive all meeting permissions from the current user alias and the meeting creator alias.
 *
 * @param currentUserAlias - The logged-in user's alias
 * @param creatorAlias - The meeting creator's alias (from URL param or scheduling data), null if no meeting context
 * @param hasSharedLink - Whether the page was loaded via a shared meeting link (meetingId + creator in URL)
 * @param coOrganizerLogin - The co-organizer's alias, if any
 */
export function getMeetingPermissions(
  currentUserAlias: string,
  creatorAlias: string | null,
  hasSharedLink: boolean,
  coOrganizerLogin?: string | null
): MeetingPermissions {
  const isCreator = !!currentUserAlias && !!creatorAlias && creatorAlias.toLowerCase() === currentUserAlias.toLowerCase();
  const isCoOrganizer = !!currentUserAlias && !!coOrganizerLogin && coOrganizerLogin.toLowerCase() === currentUserAlias.toLowerCase();
  const hasEditAccess = isCreator || isCoOrganizer;
  const isSharedCreator = hasSharedLink && hasEditAccess;
  const isSharedViewer = hasSharedLink && !hasEditAccess;

  return {
    isCreator,
    hasEditAccess,
    isSharedViewer,
    isSharedCreator,
    isSharedLink: hasSharedLink,
    isReadOnly: isSharedViewer,
    canEditSlots: hasEditAccess || !hasSharedLink
  };
}
